import { useCallback } from 'react'

import { getAddedCartQuantity } from '@/helpers/getAddedCartQuantity'
import { useLoadFullProduct } from '@/hooks/useFullProductQuery'
import { usePriceContext } from '@/hooks/usePriceContext'
import { useTrackEvent } from '@/hooks/useTrackEvent'
import { TrackingTopic } from '@/lib/eventBus/trackingTopics'

import type { OrderEntry } from '@/gen/ecom/models'
import type { useCart } from '@/hooks/useCart'
import type { CartLineItem, ListContext } from '@/lib/eventBus/trackingEvents'

/** Taken from the caller's own useCart() call: a second useCart instance per product card is expensive. */
type CartOperations = Pick<
  ReturnType<typeof useCart>,
  'cart' | 'addProduct' | 'addProductAll' | 'updateProductQuantity' | 'removeProduct'
>

interface AddToCartRequest {
  product?: CartLineItem['product']
  productCode?: string | null
  /** The cart quantity the user asked for, not the amount to add. */
  quantity: number
  listContext?: ListContext
  index?: number
}

interface QuantityChange {
  product?: CartLineItem['product']
  productCode: string
  /** Positive when the cart quantity went up, negative when it went down. */
  quantityChange: number
  listContext?: ListContext
  index?: number
}

interface AddAllToCartLine {
  product: CartLineItem['product']
  productCode: string
  quantity: number
  index?: number
}

/** The only place that writes cart quantity changes and reports add_to_cart / remove_from_cart, so callers
 * never track cart changes themselves. */
export const useCartQuantityChange = ({
  cart,
  addProduct,
  addProductAll,
  updateProductQuantity,
  removeProduct,
}: CartOperations) => {
  const { fetchProductPrice } = usePriceContext()
  const { track, trackAddToCart } = useTrackEvent()
  const loadFullProduct = useLoadFullProduct()

  const getPriceData = useCallback(
    async (productCode: string, quantity: number) =>
      (await fetchProductPrice({ productId: productCode, quantity }))?.priceData,
    [fetchProductPrice]
  )

  const trackQuantityChange = useCallback(
    async ({ product, productCode, quantityChange, listContext, index }: QuantityChange) => {
      if (quantityChange === 0) {
        return
      }

      const quantity = Math.abs(quantityChange)
      const [trackedProduct, priceData] = await Promise.all([
        product ?? loadFullProduct(productCode),
        // totalPrice is not linear in quantity, so the price is fetched for the changed quantity only.
        getPriceData(productCode, quantity),
      ])
      const items = [{ product: trackedProduct, priceData, quantity, index }]

      if (quantityChange > 0) {
        trackAddToCart(items, listContext)
      } else {
        track(TrackingTopic.CartItemRemoved, { items, listContext })
      }
    },
    [loadFullProduct, getPriceData, trackAddToCart, track]
  )

  const addToCart = useCallback(
    async ({ product, productCode = product?.code, quantity, listContext, index }: AddToCartRequest) => {
      if (productCode == null) {
        return
      }

      const existingEntry = cart?.entries?.find((entry) => entry.product?.code === productCode)

      if (existingEntry == null) {
        await addProduct(productCode, quantity)
      } else {
        await updateProductQuantity(existingEntry, quantity)
      }

      const quantityChange = getAddedCartQuantity(quantity, existingEntry?.quantity)
      void trackQuantityChange({ product, productCode, quantityChange, listContext, index })
    },
    [cart?.entries, addProduct, updateProductQuantity, trackQuantityChange]
  )

  const removeFromCart = useCallback(
    async (entry: OrderEntry, listContext: ListContext) => {
      const productCode = entry.product?.code

      if (entry.entryNumber == null) {
        return
      }

      await removeProduct(entry.entryNumber)

      if (productCode == null) {
        return
      }

      void trackQuantityChange({ productCode, quantityChange: -(entry.quantity ?? 0), listContext })
    },
    [removeProduct, trackQuantityChange]
  )

  const trackAddedLines = useCallback(
    async (lines: AddAllToCartLine[], listContext: ListContext) => {
      const items = await Promise.all(
        lines.map(async ({ product, productCode, quantity, index }) => ({
          product,
          priceData: await getPriceData(productCode, quantity),
          quantity,
          index,
        }))
      )
      trackAddToCart(items, listContext)
    },
    [getPriceData, trackAddToCart]
  )

  /** addProductAll adds on top of the cart, so each line quantity is already the added quantity. */
  const addAllToCart = useCallback(
    async (lines: AddAllToCartLine[], listContext: ListContext) => {
      if (!lines.length) {
        return
      }

      await addProductAll(lines.map(({ productCode, quantity }) => ({ productCode, quantity })))
      void trackAddedLines(lines, listContext)
    },
    [addProductAll, trackAddedLines]
  )

  return { addToCart, addAllToCart, removeFromCart }
}
